import type { Journey, JourneyType } from "./journey";

/**
 * Runtime constant for schema version — used by the upload script and by the
 * client to guard against stale cache serving an old format.
 */
export const JOURNEY_S3_SCHEMA_VERSION = "1.0" as const;
export const JOURNEY_S3_SCHEMA_VERSION_V2 = "2.0" as const;
export const JOURNEY_CONTENT_LOCALES = ["en", "es", "he"] as const;
export type JourneyContentLocale = (typeof JOURNEY_CONTENT_LOCALES)[number];

const JOURNEY_CONTENT_LOCALE_SET = new Set<string>(JOURNEY_CONTENT_LOCALES);

/**
 * Top-level shape of every `journeys/{type}/{locale}.json` file stored in S3
 * and served via CloudFront.
 *
 * The inner `journey` field reuses the existing Journey interface verbatim so
 * existing components require zero changes (D003 — restructure with version
 * envelope, no breaking field renames).
 */
export interface JourneyS3File {
	/** Compile-time-enforced literal so a schema bump is a type error. */
	schemaVersion: "1.0" | "2.0";
	/** BCP-47 locale code, e.g. 'en' or 'es'. */
	locale: string;
	/** Journey programme type — keeps the union in sync with JourneyType. */
	journeyType: JourneyType;
	/** ISO 8601 timestamp of when this file was generated (used for cache busting diagnostics). */
	generatedAt: string;
	/** Full journey data — shape is identical to what components already consume. */
	journey: Journey;
}

/** Returned by getJourney() — carries the journey data plus its origin. */
export interface JourneyFetchResult {
	journey: Journey;
	source: "cdn";
	/**
	 * The locale actually loaded from the CDN. Differs from the requested app
	 * locale when content for that locale is unavailable (e.g. requested 'es'
	 * but the journey only has 'en' content). Callers that persist the locale
	 * downstream (e.g. booking rows) must use this resolved value so we don't
	 * record an 'es' booking against a task that was loaded from en.json.
	 */
	contentLocale: JourneyContentLocale;
}

/**
 * Construct the CloudFront URL for a given journey type and content locale.
 *
 * Relies on the `VITE_JOURNEY_CDN_BASE_URL` environment variable, e.g.:
 *   https://d1example.cloudfront.net
 *
 * Resulting path pattern: `{base}/journeys/{type}/{locale}.json`
 */
export function buildCloudFrontUrl(
	type: JourneyType,
	locale: JourneyContentLocale,
): string {
	if (!JOURNEY_CONTENT_LOCALE_SET.has(locale)) {
		throw new Error(`Unsupported journey content locale: ${locale}`);
	}

	const rawBase = import.meta.env.VITE_JOURNEY_CDN_BASE_URL;
	if (!rawBase) {
		throw new Error(
			"VITE_JOURNEY_CDN_BASE_URL is not set; cannot build CloudFront URL",
		);
	}
	const base = rawBase.replace(/\/$/, "");
	return `${base}/journeys/${type}/${locale}.json`;
}
